import { useState, useEffect, useMemo } from 'react'
import { useFormik } from 'formik'

import PiggyBank from '@telus-uds/palette-allium/build/web/icons/PiggyBank'
import Delete from '@telus-uds/palette-allium/build/web/icons/Delete'

import {
  Typography,
  Box,
  FlexGrid,
  Button,
  Link,
  Notification,
  Icon,
  Spinner,
  Card,
  StackView,
  Spacer,
  TextInput
} from '@telus-uds/components-web'

import useLegacyAnalytics from '../../../hooks/useLegacyAnalytics'

import useTranslate from '../../../hooks/useTranslate'
import useCart from '../../../hooks/useCart'
import { alloyEvents } from '../../../utils/alloyAnalytics'

import {
  onPromoCodePanelOpened,
  onPromoCodeRemoved,
  onPromoCodeRemoveFailure,
  onPromoCodeApplyFailure,
  onPromoCodeInvalid,
  onPromoCodeApplied
} from '../../../utils/legacyAnalytics'

import { PromoCodeLink, StyledDiv } from './styles'

const CodeRedemption = () => {
  const translate = useTranslate()
  const {
    redeemCode,
    isRedeemingCode,
    getRedeemedCode,
    getRedeemCodeError,
    removeCode,
    isRemovingCode,
    getRemoveCodeError
  } = useCart()
  const { updateLegacyDataLayer } = useLegacyAnalytics()

  const redeemedCode = getRedeemedCode()
  const removeCodeError = getRemoveCodeError()
  const redeemCodeRequestError = getRedeemCodeError()
  const [isCodeRedemptionActive, setIsCodeRedemptionActive] = useState(!!redeemedCode)

  useEffect(() => {
    if (removeCodeError) {
      updateLegacyDataLayer(onPromoCodeRemoveFailure(redeemedCode))
      alloyEvents.errorPromoCode({
        code: redeemedCode,
        type: 'application_error',
        message: translate('serverError'),
        status: removeCodeError.status
      })
    }
  }, [removeCodeError])

  const redeemCodeForm = useFormik({
    initialValues: {
      code: ''
    },
    validate: (values) => {
      const errors = {}
      if (values.code === '') {
        errors.code = 'requiredField'
      }
      return errors
    },
    onSubmit: (values, { resetForm }) => {
      const code = values.code.toUpperCase()
      redeemCode(code, {
        onSuccess: () => {
          resetForm()
          updateLegacyDataLayer(onPromoCodeApplied(code))
          alloyEvents.redeemPromoCode({ code })
        }
      })
    }
  })

  useEffect(() => {
    if (redeemCodeRequestError) {
      const commonProps = {
        code: redeemCodeForm.values.code,
        status: redeemCodeRequestError.status
      }

      if (redeemCodeRequestError.status >= 500) {
        updateLegacyDataLayer(onPromoCodeApplyFailure(redeemCodeForm.values.code.toUpperCase()))
        alloyEvents.errorPromoCode({
          ...commonProps,
          type: 'application_error',
          message: translate('serverError')
        })
      }
      if (redeemCodeRequestError.status === 400) {
        updateLegacyDataLayer(onPromoCodeInvalid(redeemCodeForm.values.code.toUpperCase()))
        alloyEvents.errorPromoCode({
          ...commonProps,
          type: 'invalid_promo_code',
          message: translate('invalidPromoCode')
        })
      }
    }
  }, [redeemCodeRequestError])

  const [validation, feedback] = useMemo(() => {
    if (redeemCodeForm.errors.code) {
      return ['error', translate(redeemCodeForm.errors.code)]
    }
    if (redeemCodeRequestError) {
      if (redeemCodeRequestError.status >= 500) {
        return ['error', translate('serverError')]
      }
      if (redeemCodeRequestError.status === 400) {
        return ['error', translate('invalidPromoCode')]
      }
      return ['error', translate('defaultError')]
    }

    // No errors, return undefined for validation
    return [undefined, null]
  }, [redeemCodeForm.errors.code, redeemCodeRequestError, translate])

  const getSpinnerLabel = () => {
    if (isRedeemingCode) {
      return translate('validatingPromoCode')
    }
    if (isRemovingCode) {
      return translate('removingPromoCode')
    }
    return ''
  }

  const handlePromoCodeLinkClick = (e) => {
    e.preventDefault()
    setIsCodeRedemptionActive(true)
    updateLegacyDataLayer(onPromoCodePanelOpened)
    alloyEvents.openPromoCode()
  }

  const handleRemoveCode = async (e) => {
    e.preventDefault()
    updateLegacyDataLayer(onPromoCodeRemoved(redeemedCode))
    alloyEvents.removePromoCode({ code: redeemedCode })
    removeCode(redeemedCode)
    redeemCodeForm.setFieldValue('code', '', false)
  }
  return (
    <>
      <Card variant={{ padding: 'compact' }} borderRadius="md" testID="code-redemption-container">
        <Spinner label={getSpinnerLabel()} show={Boolean(isRedeemingCode || isRemovingCode)}>
          <StackView direction="row" space={2}>
            <Icon icon={PiggyBank} variant={{ color: 'brand' }} />
            <PromoCodeLink
              href="#"
              data-testid="promo-code-cta"
              onClick={(e) => handlePromoCodeLinkClick(e)}
            >
              {translate('cta')}
            </PromoCodeLink>
          </StackView>
          {isCodeRedemptionActive && (
            <Box top={4} testID="code-redemption-form">
              <FlexGrid.Row justifyContent="flex-start">
                <StyledDiv>
                  <FlexGrid.Col>
                    <Box bottom={2}>
                      <Typography variant={{ bold: true }}>{translate('label')}</Typography>
                      <Typography variant={{ size: 'small' }}>{translate('hint')}</Typography>
                    </Box>
                    <form onSubmit={redeemCodeForm.handleSubmit}>
                      <Box bottom={redeemedCode ? 0 : 3}>
                        <TextInput
                          label=""
                          testID="promo-code-input"
                          name="code"
                          onChange={(value) => redeemCodeForm.setFieldValue('code', value, true)}
                          disabled={!!redeemedCode}
                          inactive={!!redeemedCode}
                          value={redeemCodeForm.values.code.toUpperCase()}
                          validation={validation}
                          feedback={feedback}
                        />
                      </Box>
                      {!redeemedCode && (
                        <Button
                          onPress={redeemCodeForm.handleSubmit}
                          variant={{ priority: 'low', width: 'full' }}
                          testID="promo-code-applybutton"
                        >
                          {translate('button')}
                        </Button>
                      )}
                    </form>

                    {redeemedCode && (
                      <>
                        <FlexGrid.Row justifyContent="flex-start">
                          <Typography
                            variant={{ size: 'small', bold: true }}
                            testID="promo-code-applied-label"
                          >
                            {translate('appliedLabel')}
                          </Typography>
                        </FlexGrid.Row>
                        <FlexGrid.Row justifyContent="space-between">
                          <FlexGrid.Col textAlign="left">
                            <Typography
                              variant={{ size: 'small', bold: true }}
                              testID="promo-code-applied-text"
                            >
                              {redeemedCode}
                            </Typography>
                          </FlexGrid.Col>
                          <FlexGrid.Col textAlign="right">
                            <Typography variant={{ size: 'small' }}>
                              <Link
                                href="#"
                                icon={Delete}
                                onClick={handleRemoveCode}
                                testID="promo-code-removebutton"
                                variant={{ style: 'inline', size: 'small' }}
                              >
                                {translate('remove')}
                              </Link>
                            </Typography>
                          </FlexGrid.Col>
                        </FlexGrid.Row>
                        {removeCodeError && (
                          <FlexGrid.Row justifyContent="flex-start">
                            <Notification
                              variant={{ validation: 'error' }}
                              testID="remove-coupon-code-error"
                            >
                              <Typography variant={{ size: 'medium' }}>
                                {translate('serverError')}
                              </Typography>
                            </Notification>
                          </FlexGrid.Row>
                        )}
                      </>
                    )}
                  </FlexGrid.Col>
                </StyledDiv>
              </FlexGrid.Row>
            </Box>
          )}
        </Spinner>
      </Card>
      <Spacer space={5} />
    </>
  )
}

export default CodeRedemption
